import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import {
  IForgotPassword,
  ILoginEmpresa,
  ILoginFuncionario
} from './auth.model';
import { UserIpService } from '../UserIpService/userIp.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _isTokenValid: boolean = false;
  private ip: string = '';
  private readonly API_URL = `${environment.baseURL}Empresa/LoginEmpresa`;
  private readonly API_URL_FUNC = `${environment.baseURL}Funcionario/LoginFuncionario`;
  private dadoLogin = new BehaviorSubject({
    idEmpresa: '',
    nome: '',
    Token: '',
    RefreshToken: '',
    RefreshTokenExpireDate: '',
    role: ''
  });
  dadoLogin$ = this.dadoLogin.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private snackBar: MatSnackBar,
    private userIpService: UserIpService
  ) {
    this.getIpAddress();

    const rawDadoLogin = localStorage.getItem('DADO_LOGIN');
    if (!rawDadoLogin) {
      return;
    }

    this.dadoLogin.next(JSON.parse(rawDadoLogin));
  }

  get isTokenValid(): boolean {
    return this._isTokenValid;
  }

  login(email: string, password: string) {
    const body: ILoginEmpresa = {
      TX_MAIL_EPSA: email,
      TX_SENH_EPSA: password
    };

    return this.http
      .post<{
        IdRole?: number;
        NmRole?: string;
        IdEmpresa?: number;
        NmEmpresa?: string;
        NmEmail?: string;
        Token?: string;
        RefreshToken?: string;
        RefreshTokenExpireDate?: string;
      }>(this.API_URL, body)
      .pipe(
        tap(response => {
          this.saveToken(response.Token ?? '');
          const { valid, tokenDecoded } = this.isTokenValidToken(
            response.Token ?? ''
          );
          this._isTokenValid = valid;
          localStorage.setItem(
            'DADO_LOGIN',
            JSON.stringify({ ...tokenDecoded, ...response })
          );

          localStorage.setItem('nome', response.NmEmpresa ?? '');
          localStorage.setItem('roleEmpresa', response.NmRole ?? '');
          localStorage.setItem('id', String(response.IdEmpresa) ?? '');
          localStorage.setItem('role', 'externo');
          localStorage.setItem('idRole', response.IdRole?.toString() ?? '');
          localStorage.setItem('ipv4', this.ip);
          this.dadoLogin.next({ ...tokenDecoded, ...response } as any);
        }),
        catchError(error => {
          return throwError(error);
        })
      );
  }

  logout(): void {
    this.removeToken();
    this._isTokenValid = false;
    this.router.navigate(['inserir-informacoes-laudo']);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    this._isTokenValid = token !== null && this.isTokenValidToken(token).valid;
    return this._isTokenValid;
  }

  private saveToken(token: string): void {
    localStorage.setItem('token', token);
  }

  private removeToken(): void {
    localStorage.removeItem('token');
  }

  private isTokenValidToken(token: string): {
    valid: boolean;
    tokenDecoded?: any;
  } {
    try {
      const tokenDecoded = jwtDecode<{ exp: number }>(token);
      const currentTime = new Date().getTime() / 1000;
      return { valid: tokenDecoded.exp > currentTime, tokenDecoded };
    } catch {
      return { valid: false };
    }
  }

  isAuthenticatedAndTokenValid() {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    const tokenData = this.getTokenData(token);
    const expiration = new Date(tokenData.exp * 1000);

    return expiration > new Date();
  }

  private getTokenData(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const decodedData = JSON.parse(atob(base64));
    return decodedData;
  }

  private openSnackBar(message: string, action: string): void {
    this.snackBar.open(message, action, { panelClass: ['error-snackbar'] });
  }

  public forgotPassword(body: IForgotPassword): Observable<any> {
    return this.http.post<any>(
      `${environment.baseURL}Email/SendForgotPasswordEmail`,
      body
    );
  }

  getIpAddress(): void {
    const res = this.userIpService.getIpAddress().subscribe({
      next: (res: any) => {
        this.ip = res.ip;
      }
    });
  }
}
